/**
 * CPOC-154 (dev only): keep the rep's desk current without a shopper message.
 * Small pure helpers for the chat window; every caller checks siteValuesEnabled(env) first,
 * so on any other env none of this runs and nothing changes.
 */
/** Retry waits (ms) after a journey send the rep could not take yet. */
export const JOURNEY_RETRY_MS = [2000, 5000, 15000] as const;
/** The slow re-ask timer while a handoff is live and the tab is visible. */
export const CONTEXT_REASK_MS = 60_000;

/** Minimum gap (ms) between two re-asks of the site page. */
export const REASK_MIN_GAP_MS = 2000;

/** Pure gate: true when no re-ask has happened yet or the last one was at least `minGap` ms ago. */
export function reaskAllowed(now: number, last: number | null, minGap = REASK_MIN_GAP_MS): boolean {
  return last === null || now - last >= minGap;
}

/** Only these reasons are worth another try; `unchanged` and `sent` are not. */
export function retryableReason(reason: unknown): boolean {
  return reason === 'not_joined' || reason === 'send_failed';
}

/** Ask the parent page for fresh context. The recorder already records the TRACI_PAGE_CONTEXT answer. */
export function requestSiteContext(win: Window | undefined, targetOrigin: string): boolean {
  if (!win || win.parent === win) return false;
  try {
    win.parent.postMessage({ type: 'TRACI_REQUEST_CONTEXT', source: 'AI-Assistant' }, targetOrigin);
    return true;
  } catch { return false; }
}

/** Tell the parent page a handoff is on or off (the Website Team's script keys on this). */
export function postHandoffActive(win: Window | undefined, targetOrigin: string, active: boolean, now = Date.now()): boolean {
  if (!win || win.parent === win) return false;
  try {
    win.parent.postMessage({ source: 'TRACI', type: 'traci_handoff_active', active, at: now }, targetOrigin);
    return true;
  } catch { return false; }
}

const PLATFORMS = ['Windows', 'macOS', 'Android', 'iOS', 'Linux', 'Chrome OS', 'iPadOS'];
export type OwnVisitHint = { touch: boolean; platform?: string; mobile?: boolean };

/** A tiny, type-checked device hint from navigator. The server maps it to an allowlist. */
export function ownVisitHint(nav: any = typeof navigator !== 'undefined' ? navigator : undefined): OwnVisitHint | null {
  if (!nav) return null;
  const out: OwnVisitHint = { touch: typeof nav.maxTouchPoints === 'number' && nav.maxTouchPoints > 1 };
  const uad = nav.userAgentData;
  if (uad && typeof uad.platform === 'string' && PLATFORMS.includes(uad.platform)) out.platform = uad.platform;
  if (uad && typeof uad.mobile === 'boolean') out.mobile = uad.mobile;
  return out;
}

/** Fires `cb` on the edge where the width goes from 0 to above 0 (the chat frame became visible), debounced. */
export function createVisibleEdgeDetector(cb: () => void, debounceMs = 300): { observe: (width: number) => void; dispose: () => void } {
  let wasVisible: boolean | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  return {
    observe(width: number) {
      const visible = width > 0;
      const rising = wasVisible === false && visible;
      wasVisible = visible;
      if (!rising) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { timer = null; cb(); }, debounceMs);
    },
    dispose() { if (timer) clearTimeout(timer); timer = null; },
  };
}
